import React, { useCallback, useEffect, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTheme } from '../../contexts/ThemeContext';
import {
  COMPANION_HIGHLIGHT_CLEAR_EVENT,
  COMPANION_HIGHLIGHT_EVENT,
  type CompanionHighlightDetail,
} from '../../utils/companionHighlight';

type ActiveHighlight = CompanionHighlightDetail & {
  rect: { top: number; left: number; width: number; height: number } | null;
};

const HIGHLIGHT_CLASS = 'mmec-companion-highlight-target';

function findSymbolOnPage(symbol: string): Element | null {
  const sym = symbol.trim().toUpperCase();
  if (!sym || sym.length > 8) return null;
  const selectors =
    '[data-ticker], [data-symbol], [data-underlying], .ticker, .symbol-cell, td, th, span, button, a, strong';
  const nodes = document.querySelectorAll(selectors);
  for (let i = 0; i < nodes.length; i++) {
    const el = nodes[i];
    const text = (el.textContent || '').trim().toUpperCase();
    if (text === sym || text.startsWith(`${sym} `) || text.startsWith(`${sym}/`)) {
      const r = el.getBoundingClientRect();
      if (r.width > 0 && r.height > 0 && r.top < window.innerHeight && r.bottom > 0) {
        return el;
      }
    }
  }
  return null;
}

function rectForDetail(detail: CompanionHighlightDetail): ActiveHighlight['rect'] {
  if (detail.selector) {
    try {
      const el = document.querySelector(detail.selector);
      if (el) {
        const r = el.getBoundingClientRect();
        return { top: r.top, left: r.left, width: r.width, height: r.height };
      }
    } catch {
      /* invalid selector */
    }
  }
  if (detail.symbol) {
    const el = findSymbolOnPage(detail.symbol);
    if (el) {
      el.classList.add(HIGHLIGHT_CLASS);
      const r = el.getBoundingClientRect();
      return { top: r.top, left: r.left, width: r.width, height: r.height };
    }
  }
  return null;
}

const CompanionHighlightOverlay: React.FC = () => {
  const { language } = useLanguage();
  const { colors } = useTheme();
  const ar = language === 'ar';
  const [active, setActive] = useState<ActiveHighlight | null>(null);

  const clearDomMarks = useCallback(() => {
    document.querySelectorAll(`.${HIGHLIGHT_CLASS}`).forEach((el) => {
      el.classList.remove(HIGHLIGHT_CLASS);
    });
  }, []);

  const dismiss = useCallback(() => {
    clearDomMarks();
    setActive(null);
  }, [clearDomMarks]);

  useEffect(() => {
    const onHighlight = (ev: Event) => {
      clearDomMarks();
      const detail = (ev as CustomEvent<CompanionHighlightDetail>).detail;
      if (!detail) return;
      const rect = rectForDetail(detail);
      setActive({ ...detail, rect });
    };
    const onClear = () => dismiss();
    window.addEventListener(COMPANION_HIGHLIGHT_EVENT, onHighlight);
    window.addEventListener(COMPANION_HIGHLIGHT_CLEAR_EVENT, onClear);
    return () => {
      window.removeEventListener(COMPANION_HIGHLIGHT_EVENT, onHighlight);
      window.removeEventListener(COMPANION_HIGHLIGHT_CLEAR_EVENT, onClear);
      clearDomMarks();
    };
  }, [clearDomMarks, dismiss]);

  useEffect(() => {
    if (!active?.rect) return;
    const reposition = () => {
      if (!active.symbol && !active.selector) return;
      const rect = rectForDetail(active);
      if (rect) setActive((prev) => (prev ? { ...prev, rect } : null));
    };
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [active?.symbol, active?.selector]);

  if (!active) return null;

  const { rect, symbol, message } = active;
  const pad = 8;

  return (
    <>
      <style>{`
        .${HIGHLIGHT_CLASS} {
          outline: 3px solid ${colors.primary} !important;
          outline-offset: 2px;
          border-radius: 4px;
          animation: mmecSpotlightPulse 1.2s ease-in-out infinite;
        }
        @keyframes mmecSpotlightPulse {
          0%, 100% { box-shadow: 0 0 0 0 ${colors.primary}55; }
          50% { box-shadow: 0 0 0 6px ${colors.primary}22; }
        }
      `}</style>
      <div
        role="presentation"
        onClick={dismiss}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 10040,
          backgroundColor: 'rgba(0,0,0,0.35)',
          pointerEvents: 'auto',
        }}
      />
      {rect ? (
        <div
          aria-hidden
          style={{
            position: 'fixed',
            top: rect.top - pad,
            left: rect.left - pad,
            width: rect.width + pad * 2,
            height: rect.height + pad * 2,
            borderRadius: 8,
            border: `3px solid ${colors.primary}`,
            boxShadow: `0 0 0 9999px rgba(0,0,0,0.35), 0 0 24px ${colors.primary}88`,
            zIndex: 10041,
            pointerEvents: 'none',
          }}
        />
      ) : null}
      <div
        role="status"
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'fixed',
          bottom: rect ? Math.min(rect.top - 12, window.innerHeight - 120) : 100,
          left: rect ? Math.min(Math.max(16, rect.left), window.innerWidth - 320) : 16,
          maxWidth: 300,
          zIndex: 10042,
          padding: '12px 14px',
          borderRadius: 10,
          backgroundColor: 'var(--elev)',
          border: '1px solid var(--border)',
          boxShadow: 'var(--lift-3)',
          fontFamily: 'var(--font-ui)',
          direction: ar ? 'rtl' : 'ltr',
        }}
      >
        <div style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--acc)', marginBottom: 4 }}>
          {ar ? 'مرافق MMEC' : 'MMEC Companion'}
        </div>
        {symbol ? (
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--txt)' }}>{symbol}</div>
        ) : null}
        {message ? (
          <p style={{ margin: '6px 0 0', fontSize: 13, lineHeight: 1.45, color: 'var(--txt-2)' }}>
            {message}
          </p>
        ) : null}
        {!rect && symbol ? (
          <p style={{ margin: '8px 0 0', fontSize: 12, color: 'var(--muted)' }}>
            {ar
              ? 'لم أجد الرمز على الشاشة الحالية — انتقل لتبويب التقرير أو اسألني في المحادثة.'
              : 'Symbol not visible on this screen — open Elite Pulse or ask me in chat.'}
          </p>
        ) : null}
        <button
          type="button"
          onClick={dismiss}
          style={{
            marginTop: 10,
            height: 28,
            padding: '0 12px',
            fontSize: 12,
            fontWeight: 500,
            fontFamily: 'var(--font-ui)',
            borderRadius: 8,
            border: 'none',
            cursor: 'pointer',
            backgroundColor: 'var(--acc)',
            color: 'var(--void)',
          }}
        >
          {ar ? 'حسناً' : 'Got it'}
        </button>
      </div>
    </>
  );
};

export default CompanionHighlightOverlay;
